import React, { useEffect } from 'react';
import { PaystackButton } from 'react-paystack';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createPayment, resetPayment } from '../../redux/actions/payment';
import { bookAppointmentAction } from "../../redux/actions/appointmentActions";
import { fetchHospitalPaymentConfigAction } from '../../redux/actions/userActions';
import { toast } from '../../utils/toast';
import { baseUrl, PAYSTACK_PUBLIC_KEY } from '../../redux/actions/baseUrl';

export default function Paystack({ 
    amount, 
    email, 
    phone, 
    notes, 
    url, 
    compData, 
    paymentType = 'general', // 'appointment' or 'general'
    onPaymentSuccess,
    onPaymentError,
    buttonText = 'Pay Now',
    buttonClassName = 'bg-primary100 w-full text-white text-xl text-bold mt-5 px-16 py-3 rounded-xl',
    verificationEndpoint = null,
    channels = ['card', 'bank', 'bank_transfer', 'ussd', 'qr']
}) {
    const dispatch = useDispatch();
    const { loading, success, data, error } = useSelector((state) => state.createPayment);
    const appt = useSelector((state) => state.bookAppontment);
    const loginUserState = useSelector((state) => state.loginUser);

    const navigate = useNavigate();
    console.log('loginUserState:', loginUserState);
    const fullUserInfo = loginUserState?.userInfo;
    const patient = fullUserInfo?.patient || JSON.parse(localStorage.getItem('userInfo')) || {};
    const hospital = fullUserInfo?.hospital || {};
    const paymentConfig = hospital?.payment_config;
    const hasSubAccount = !!(paymentConfig?.has_sub_account && paymentConfig?.sub_account_info?.is_active);
    const subAccountCode = paymentConfig?.sub_account_info?.sub_account_code;
    const revenueModel = paymentConfig?.sub_account_info?.revenue_model;
    const bearerType = revenueModel?.bearer_type || paymentConfig?.sub_account_info?.bearer_type; // 'account' or 'subaccount'

    const [paymentState, setPaymentState] = React.useState({
        isProcessing: false,
        paymentReference: null,
        configFetched: false
    });

    // Fetch latest payment config on component mount
    useEffect(() => {
        const fetchPaymentConfig = async () => {
            if (!paymentState.configFetched && fullUserInfo?.hospital) {
                try {
                    await dispatch(fetchHospitalPaymentConfigAction());
                    setPaymentState(prev => ({ ...prev, configFetched: true }));
                } catch (error) {
                    console.warn('Failed to fetch updated payment config, using cached data:', error);
                    setPaymentState(prev => ({ ...prev, configFetched: true }));
                }
            }
        };

        fetchPaymentConfig();
    }, [dispatch, fullUserInfo?.hospital, paymentState.configFetched]);

    // Build Paystack config with split settings based on revenue model
    const splitProps = (() => {
        // Don't initialize payment until config is fetched
        if (!paymentState.configFetched) {
            return {};
        }

        if (!hasSubAccount || !subAccountCode || !revenueModel?.type) return {};

        const amountInKobo = Math.round(Number(amount) * 100);
        const isValidBearer = bearerType === 'account' || bearerType === 'subaccount';

        if (revenueModel.type === 'percentage') {
            const props = { subaccount: subAccountCode };
            if (isValidBearer) props.bearer = bearerType;
            return props; 
        }

        if (revenueModel.type === 'fixed_fee') {
            const fixedFee = revenueModel.platform_fixed_fee; 
            const feeNaira = typeof fixedFee === 'string' ? parseFloat(fixedFee) : fixedFee;
            const feeInKobo = feeNaira && !Number.isNaN(feeNaira) ? Math.round(feeNaira * 100) : null;

            if (feeInKobo && feeInKobo > 0) {
                const maxCharge = Math.max(amountInKobo - 100, 0);
                const transactionCharge = Math.min(feeInKobo, maxCharge);
                const props = {
                    subaccount: subAccountCode,
                    transaction_charge: transactionCharge,
                };
                if (isValidBearer) props.bearer = bearerType;
                return props;
            }
            const props = { subaccount: subAccountCode };
            if (isValidBearer) props.bearer = bearerType;
            return props;
        }
        return {};
    })();

    const config = {
        reference: (new Date()).getTime().toString(),
        email: email || patient?.email,
        amount: Math.round(Number(amount) * 100),
        publicKey: PAYSTACK_PUBLIC_KEY,
        channels: channels,
        metadata: {
            payment_type: paymentType,
            hospital_id: hospital?.id || patient?.hospital_id,
            patient_id: patient?.id,
            clinic_id: compData?.clinic_id,
            clinic_name: compData?.clinic_name,
            appt_date: compData?.appt_date,
            appt_time: compData?.appt_time,
            appt_mode: compData?.appt_mode,
            appt_type: compData?.appt_type || (paymentType === 'appointment' ? 'online' : null),
            appt_url: compData?.appt_url,
            test_ids: compData?.test_ids || null,
            location: compData?.location || patient?.location || 1,
            notes: notes || compData?.notes,
            processing_fee: revenueModel?.platform_fixed_fee ? Number(revenueModel.platform_fixed_fee) : 0,
        },
        ...splitProps,
    };

    const handlePaystackSuccessAction = (reference) => {
        setPaymentState(prev => ({
            ...prev,
            isProcessing: true,
            paymentReference: reference.reference,
        }));

        const userLocation = patient.location || patient.address || 'Not specified';

        if (paymentType === 'custom' || paymentType === 'lab_order' || (onPaymentSuccess && paymentType !== 'appointment')) {
            if (onPaymentSuccess) {
                onPaymentSuccess(reference);
            }
            setPaymentState(prev => ({
                ...prev,
                isProcessing: false,
                paymentReference: null,
            }));
            return;
        }

        if (paymentType === 'appointment' && compData) {
            dispatch(createPayment(
                patient.id,
                amount,
                reference.reference,
                userLocation,
                notes,
                compData
            ));
        } else {
            dispatch(createPayment(
                patient.id,
                amount,
                reference.reference,
                userLocation,
                notes
            ));
        }
    };

    const handlePaystackCloseAction = () => {
        const errorMessage = 'Payment was cancelled or failed';
        toast.error(errorMessage, {
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
        });
        setPaymentState(prev => ({
            ...prev,
            isProcessing: false,
            paymentReference: null
        }));
        if (onPaymentError) {
            onPaymentError(errorMessage);
        }
    };

    const componentProps = {
        ...config,
        text: buttonText,
        onSuccess: (reference) => handlePaystackSuccessAction(reference),
        onClose: handlePaystackCloseAction,
    };

    useEffect(() => {
        if (success && paymentState.isProcessing) {
            toast.success('Payment successful!', {
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });

            if (paymentType === 'appointment' && compData) {
                if (!appt.loading && !appt.success) {
                    dispatch(bookAppointmentAction(compData));
                }
            } else if (url) {
                dispatch(resetPayment());
                navigate(`/${url}`, { 
                    state: { 
                        data: compData,
                        paymentReference: paymentState.paymentReference,
                        paymentData: data
                    } 
                });
            }

            setPaymentState(prev => ({
                ...prev,
                isProcessing: false,
                paymentReference: null
            }));

            if (onPaymentSuccess) {
                onPaymentSuccess({ reference: paymentState.paymentReference, paymentData: data });
            }
        }

        if (error && paymentState.isProcessing) {
            toast.error(error || 'Payment processing failed', {
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });

            setPaymentState(prev => ({
                ...prev,
                isProcessing: false,
                paymentReference: null
            }));

            if (onPaymentError) {
                onPaymentError(error);
            }
        }
    }, [success, error, paymentState.isProcessing]);

    useEffect(() => {
        if (appt.success && paymentType === 'appointment' && success) {
            toast.success('Appointment booked successfully!', {
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });

            if (url) {
                dispatch(resetPayment());
                dispatch({ type: "BOOK_APPOINTMENT_RESET" });
                navigate(`/${url}`, { 
                    state: { 
                        data: compData,
                        paymentReference: paymentState.paymentReference,
                        appointmentData: appt.data
                    } 
                });
            }
        }

        if (appt.error && paymentType === 'appointment') {
            toast.error(appt.error || 'Failed to book appointment', {
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
        }
    }, [appt.success, appt.error]);

    return (
        <PaystackButton 
            className={buttonClassName}
            disabled={!paymentState.configFetched || loading || appt.loading || paymentState.isProcessing}
            {...componentProps} 
        />
    );
}
